/**
 * Responsibility: Own admin preforge bundle capture and direct prefoged-reality launch.
 * Scope: Bundle export/import orchestration only; admin UI state and navigation stay elsewhere.
 */
import '../ai/config'
import * as gameStore from '../game-store.js'
import { exportBlueprint, exportStarterKit, exportVesselProfile } from '../engine/persist'
import { locationKey } from '../engine/state'
import { getAppImportMetaEnv } from "@anyquest/shared-core"
import { router } from '../ai/router'
import { isAIError } from '../ai/errors'
import { extractJson } from '../ai/json-utils'
import { runWorldForge, type WorldForgeNode } from '../ai/logicForge'
import { PREFORGE_DRAFT_SYSTEM, buildPreforgeDraftPrompt } from '../ai/prompts'
import { loadAIConfig } from '../ai/config'
import { decodeModelRef, loadAIModelRoutingState, jobTypeToSlot } from '../ai/model-settings'
import {
  buildPreforgeRecipeDefaults,
  normalizePreforgedOffer,
  type CommercePlanId,
  type PreforgeAiProviderId,
  type PreforgedOffer,
  type PreforgedOfferPriceModel,
  type PreforgedOfferState,
  type PreforgeForgeReport,
  type PreforgeDraft,
  type PreforgeDraftAnalysis,
  type PreforgeDraftTextlets,
  type PreforgeSourceWorldRef,
  type PreforgeRecipe,
} from '../commerce/commerce-contract'
import type { AtlasEdge, AtlasNode, GameState, NPC, ProgressClock, Quest, WorldNPC } from "@anyquest/shared-core"

export interface PreforgeCaptureRequest {
  id: string
  title: string
  summary?: string
  includedPlanId?: CommercePlanId
  priceModel?: PreforgedOfferPriceModel
  priceCents?: number
  currency?: string
  publishState?: PreforgedOfferState
  forgeRecipe?: PreforgeRecipe
}

export interface PreforgeCaptureResult {
  offer: PreforgedOffer
  worldCode: string
  seedCode: string
  vesselCode: string
  starterCode: string
}

export interface PreforgeCodes {
  worldCode: string
  seedCode: string
  vesselCode: string
  starterCode: string
}

export interface PreforgeLaunchResult {
  saveId: string
}

export interface PreforgeDraftExpansionRequest {
  brief: string
  sourceWorld: PreforgeSourceWorldRef
  recipe: PreforgeRecipe
  draft?: Partial<PreforgeDraft>
  onProgress?: (event: PreforgeDraftProgressEvent) => void
  allowMockProvider?: boolean
}

export interface PreforgeDraftExpansionResult {
  draft: PreforgeDraft
  recipe: PreforgeRecipe
  sourceWorld: PreforgeSourceWorldRef
  notes: string[]
}

export type PreforgeDraftProgressStage =
  | 'reading-brief'
  | 'building-prompt'
  | 'requesting-draft'
  | 'receiving-draft'
  | 'normalizing-draft'
  | 'blocked'
  | 'ready'

export type PreforgeForgeFailureKind =
  | 'mock-route'
  | 'no-provider'
  | 'provider-missing'
  | 'provider-unavailable'
  | 'provider-disallowed'
  | 'sparse-response'
  | 'unknown'

export interface PreforgeForgeDiagnosticsProvider {
  providerId: string
  label: string
  enabled: boolean
  available: boolean
  selectedModel: string
  selectedRef: string
}

export interface PreforgeForgeDiagnostics {
  jobSlot: string
  requestedProviderId: PreforgeAiProviderId
  requestedModel: string
  routedProviderId: string
  routedModel: string
  activeProviderId: string
  providers: PreforgeForgeDiagnosticsProvider[]
  likelyCause: 'settings' | 'bug' | 'unknown'
}

export class PreforgeForgeError extends Error {
  readonly kind: PreforgeForgeFailureKind
  readonly diagnostics: PreforgeForgeDiagnostics

  constructor(kind: PreforgeForgeFailureKind, message: string, diagnostics: PreforgeForgeDiagnostics) {
    super(message)
    this.name = 'PreforgeForgeError'
    this.kind = kind
    this.diagnostics = diagnostics
  }
}

export interface PreforgeDraftProgressEvent {
  stage: PreforgeDraftProgressStage
  message: string
}

function normalizeBundleId(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

function countAtlasNodes(sourceState: typeof gameStore.state): number {
  return Object.keys(sourceState?.atlas?.nodes ?? {}).length
}

function countAtlasItems(sourceState: typeof gameStore.state): number {
  return Object.values(sourceState?.atlas?.nodes ?? {}).reduce((sum, node) => sum + ((node?.items?.length ?? 0)), 0)
}

function slugify(text: string, fallback = 'preforge'): string {
  const value = String(text ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return value || fallback
}

function hashText(...parts: string[]): number {
  let hash = 0
  for (const part of parts) {
    for (let i = 0; i < part.length; i += 1) {
      hash = ((hash << 5) - hash) + part.charCodeAt(i)
      hash |= 0
    }
  }
  return Math.abs(hash)
}

function compactPhrases(...phrases: string[]): string[] {
  return phrases
    .flatMap(phrase => String(phrase ?? '').split(/[\n,;•|/]+/g))
    .map(phrase => phrase.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
}

function titleCase(text: string): string {
  return String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function pickFrom<T>(seed: string, options: readonly T[]): T {
  return options[hashText(seed) % options.length]
}

function buildTopicLabel(text: string, fallback: string): string {
  const phrase = compactPhrases(text)[0]
  if (!phrase) return fallback
  return titleCase(phrase).slice(0, 80)
}

function buildTopicSentence(prefix: string, body: string, suffix: string): string {
  const trimmed = compactPhrases(body)[0] || body.trim()
  if (!trimmed) return `${prefix} ${suffix}`.trim()
  return `${prefix} ${trimmed}${suffix ? ` ${suffix}` : ''}`.trim()
}

function buildPreforgeItems(draft: PreforgeDraft, count: number, keySeed: string): string[] {
  const source = compactPhrases(draft.textlets.itemHooks, draft.textlets.worldLore, draft.brief)
  const fallback = compactPhrases('brass key', 'sealed map', 'proof token', 'latch tool', 'archive seal')
  const pool = source.length ? source : fallback
  const items: string[] = []
  for (let i = 0; i < Math.max(0, count); i += 1) {
    const base = pool[(hashText(keySeed, String(i)) % pool.length)] ?? pool[0]
    const label = titleCase(base)
    if (!items.includes(label)) items.push(label)
  }
  return items
}

function buildPreforgeNpcName(seed: string, index: number): string {
  const first = pickFrom(`${seed}:name:${index}`, ['Archivist', 'Warden', 'Guide', 'Caretaker', 'Chronicler', 'Surveyor', 'Sentinel', 'Runner'])
  const second = pickFrom(`${seed}:role:${index}`, ['Of Keys', 'Of Doors', 'Of Echoes', 'Of Lanterns', 'Of Seals', 'Of Routes', 'Of Names', 'Of Thresholds'])
  return `${first} ${second}`.trim()
}

function buildPreforgeWorldNpcs(draft: PreforgeDraft, roomKey: string, extraKeys: string[]): WorldNPC[] {
  const topics = compactPhrases(draft.textlets.npcThreads, draft.textlets.worldLore, draft.brief)
  const pool = topics.length ? topics : compactPhrases('caretakers', 'auditors', 'guides', 'watchers')
  return pool.slice(0, 4).map((topic, index) => {
    const name = buildPreforgeNpcName(topic, index)
    return {
      id: `${slugify(draft.seed.code || draft.seed.title || 'preforge')}-npc-${index + 1}`,
      name,
      identity: titleCase(topic),
      lore: `${topic}. ${draft.textlets.worldLore || draft.brief}`.trim(),
      sex: ['female', 'male'].includes(draft.vessel.sex) ? draft.vessel.sex : 'unspecified',
      intentions: [
        `Protect the opening route through ${buildTopicLabel(draft.textlets.roomSeeds || draft.brief, 'the opening route')}.`,
        `Steer testers toward ${buildTopicLabel(draft.textlets.questThreads || draft.brief, 'the larger thread')}.`,
      ],
      feelings: {},
      location: extraKeys[index] || roomKey,
      relationship: 0,
      memory: [draft.brief || draft.textlets.worldLore || 'Seeded for a tester-ready reality.'],
      inventory: buildPreforgeItems(draft, index === 0 ? 2 : 1, `${roomKey}:${index}`),
    }
  })
}

function buildPreforgeRoomNpcs(draft: PreforgeDraft): NPC[] {
  const topics = compactPhrases(draft.textlets.npcThreads, draft.textlets.worldLore, draft.brief)
  const pool = topics.length ? topics : compactPhrases('caretaker', 'archivist', 'guide')
  return pool.slice(0, 3).map((topic, index) => ({
    name: buildPreforgeNpcName(topic, index),
    identity: titleCase(topic),
    lore: `${topic}. ${draft.textlets.worldLore || draft.brief}`.trim(),
    sex: ['female', 'male'].includes(draft.vessel.sex) ? draft.vessel.sex : 'unspecified',
    stance: 'Open',
    intentions: [
      `Open the path into ${buildTopicLabel(draft.textlets.roomSeeds || draft.brief, 'the world')}.`,
      `Trade hints about ${buildTopicLabel(draft.textlets.questThreads || draft.brief, 'the quest threads')}.`,
    ],
    feelings: {},
    relationship: 0,
    memory: [draft.textlets.openingPitch || draft.brief || 'Made ready for testers.'],
    inventory: buildPreforgeItems(draft, index === 0 ? 2 : 1, `${topic}:${index}`),
  }))
}

function buildPreforgeQuestThreads(draft: PreforgeDraft): Quest[] {
  const topics = compactPhrases(draft.textlets.questThreads, draft.textlets.openingPitch, draft.brief)
  const pool = topics.length ? topics : compactPhrases('find the first seal', 'map the hidden route', 'wake the wardens')
  const created = Date.now()
  return pool.slice(0, 3).map((topic, index) => ({
    id: `${slugify(draft.seed.code || draft.seed.title || 'preforge')}-quest-${index + 1}`,
    title: titleCase(topic) || `Thread ${index + 1}`,
    desc: `${topic}. ${draft.textlets.worldLore || draft.brief}`.trim(),
    type: index === 0 ? 'quest' : 'mini-quest',
    status: 'potential',
    created,
  }))
}

function buildPreforgeProgressClocks(draft: PreforgeDraft): ProgressClock[] {
  const topics = compactPhrases(draft.textlets.questThreads, draft.textlets.worldLore, draft.brief)
  const pool = topics.length ? topics : compactPhrases('opening route', 'world pressure', 'tester momentum')
  return pool.slice(0, 2).map((topic, index) => ({
    id: `${slugify(draft.seed.code || draft.seed.title || 'preforge')}-clock-${index + 1}`,
    label: titleCase(topic) || `Clock ${index + 1}`,
    segments: 4 + index,
    filled: 0,
    trigger: `Advance ${topic}`,
  }))
}

function dedupeNpcs(npcs: NPC[]): NPC[] {
  const seen = new Set<string>()
  const deduped: NPC[] = []
  for (const npc of npcs) {
    const key = npc.name.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    deduped.push(npc)
  }
  return deduped
}

function dedupeWorldNpcs(npcs: WorldNPC[]): WorldNPC[] {
  const seen = new Set<string>()
  const deduped: WorldNPC[] = []
  for (const npc of npcs) {
    const key = npc.id.trim().toLowerCase() || npc.name.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    deduped.push(npc)
  }
  return deduped
}

function toRoomNpc(worldForgeNpc: WorldForgeNode['npcs'][number]): NPC {
  return {
    name: worldForgeNpc.name,
    identity: worldForgeNpc.identity,
    lore: worldForgeNpc.lore,
    sex: ['female', 'male'].includes(String(worldForgeNpc.sex ?? '').toLowerCase())
      ? String(worldForgeNpc.sex).toLowerCase()
      : 'unspecified',
    stance: 'Open',
    intentions: Array.isArray(worldForgeNpc.intentions) ? worldForgeNpc.intentions : [],
    feelings: {},
    relationship: Number.isFinite(worldForgeNpc.relationship) ? worldForgeNpc.relationship : 0,
    memory: Array.isArray(worldForgeNpc.memory) ? worldForgeNpc.memory : [],
    inventory: Array.isArray(worldForgeNpc.inventory) ? worldForgeNpc.inventory : [],
  }
}

const E2E_MOCK_MODE = getAppImportMetaEnv().VITE_E2E_MOCK_MODE === '1'

function isPreforgeDraftShapeAdequate(raw: unknown): raw is Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return false
  const entry = raw as Record<string, unknown>
  const brief = String(entry.brief ?? '').trim()
  const intentSummary = String(entry.intentSummary ?? '').trim()
  const analysis = entry.analysis && typeof entry.analysis === 'object' ? entry.analysis as Record<string, unknown> : null
  const seed = entry.seed && typeof entry.seed === 'object' ? entry.seed as Record<string, unknown> : null
  const vessel = entry.vessel && typeof entry.vessel === 'object' ? entry.vessel as Record<string, unknown> : null
  const textlets = entry.textlets && typeof entry.textlets === 'object' ? entry.textlets as Record<string, unknown> : null
  const textletCount = textlets
    ? [
        String(textlets.worldLore ?? '').trim(),
        String(textlets.openingPitch ?? '').trim(),
        String(textlets.npcThreads ?? '').trim(),
        String(textlets.questThreads ?? '').trim(),
        String(textlets.itemHooks ?? '').trim(),
        String(textlets.roomSeeds ?? '').trim(),
      ].filter(Boolean).length
    : 0
  return !!(
    brief
    && intentSummary
    && analysis
    && String(analysis.scopeSummary ?? '').trim()
    && Array.isArray(analysis.keyEntities) && analysis.keyEntities.length > 0
    && seed
    && String(seed.code ?? '').trim()
    && String(seed.title ?? '').trim()
    && vessel
    && String(vessel.code ?? '').trim()
    && String(vessel.name ?? '').trim()
    && String(vessel.identity ?? '').trim()
    && textlets
    && textletCount >= 3
  )
}

function isMockProviderAllowed(request: PreforgeDraftExpansionRequest): boolean {
  return request.allowMockProvider ?? E2E_MOCK_MODE
}

function isProviderMock(): boolean {
  return String(router.getActiveProviderId() ?? '').trim() === 'mock'
}

function buildPreforgeStrictFailureMessage(reason: 'provider' | 'route' | 'shape'): string {
  if (reason === 'shape') {
    return 'The AI response was too sparse to trust for forging. Open AI settings and try again.'
  }
  if (reason === 'route') {
    return 'The forge routed to the mock provider instead of a real one. Open AI settings and verify provider access.'
  }
  return 'No capable AI provider was available for forging. Open AI settings and try again.'
}

function providerLabel(providerId: string): string {
  switch (providerId) {
    case 'vertex-managed': return 'Managed Cloud AI'
    case 'gemini': return 'Google Gemini'
    case 'openai': return 'OpenAI'
    case 'anthropic': return 'Anthropic Claude'
    case 'groq': return 'Groq'
    case 'ollama': return 'Ollama (Local)'
    case 'mock': return 'Mock provider'
    case 'auto': return 'Auto'
    default: return providerId
  }
}

function formatProviderRef(ref: string): string {
  const decoded = decodeModelRef(ref)
  if (!decoded) return 'unset'
  return decoded.model ? `${providerLabel(decoded.providerId)} / ${decoded.model}` : providerLabel(decoded.providerId)
}

async function buildPreforgeDiagnostics(
  request: PreforgeDraftExpansionRequest,
  kind: PreforgeForgeFailureKind,
  routedProviderId: string,
  error?: unknown,
): Promise<PreforgeForgeDiagnostics> {
  const config = loadAIConfig()
  const routing = loadAIModelRoutingState()
  const status = await router.getStatus().catch(() => ({} as Record<string, boolean>))
  const jobSlot = jobTypeToSlot('preforge_draft') || 'quality'
  const requestedProviderId = request.recipe.aiSelection.providerId
  const requestedModel = String(request.recipe.aiSelection.model ?? '').trim()
  const routedRef = routing.assignments[`job:${jobSlot}` as const] || ''
  const routedDecoded = decodeModelRef(routedRef)
  const providers: PreforgeForgeDiagnosticsProvider[] = [
    {
      providerId: 'vertex-managed',
      label: providerLabel('vertex-managed'),
      enabled: true,
      available: !!status['vertex-managed'],
      selectedModel: formatProviderRef(routing.assignments['provider:vertex-managed' as const] || ''),
      selectedRef: routing.assignments['provider:vertex-managed' as const] || '',
    },
    {
      providerId: 'gemini',
      label: providerLabel('gemini'),
      enabled: !!config.geminiEnabled,
      available: !!status.gemini,
      selectedModel: formatProviderRef(routing.assignments['provider:gemini' as const] || ''),
      selectedRef: routing.assignments['provider:gemini' as const] || '',
    },
    {
      providerId: 'openai',
      label: providerLabel('openai'),
      enabled: !!config.openaiEnabled,
      available: !!status.openai,
      selectedModel: formatProviderRef(routing.assignments['provider:openai' as const] || ''),
      selectedRef: routing.assignments['provider:openai' as const] || '',
    },
    {
      providerId: 'anthropic',
      label: providerLabel('anthropic'),
      enabled: !!config.anthropicEnabled,
      available: !!status.anthropic,
      selectedModel: formatProviderRef(routing.assignments['provider:anthropic' as const] || ''),
      selectedRef: routing.assignments['provider:anthropic' as const] || '',
    },
    {
      providerId: 'groq',
      label: providerLabel('groq'),
      enabled: !!config.groqEnabled,
      available: !!status.groq,
      selectedModel: formatProviderRef(routing.assignments['provider:groq' as const] || ''),
      selectedRef: routing.assignments['provider:groq' as const] || '',
    },
    {
      providerId: 'ollama',
      label: providerLabel('ollama'),
      enabled: !!config.ollamaEnabled,
      available: !!status.ollama,
      selectedModel: formatProviderRef(routing.assignments['provider:ollama' as const] || ''),
      selectedRef: routing.assignments['provider:ollama' as const] || '',
    },
    {
      providerId: 'mock',
      label: providerLabel('mock'),
      enabled: true,
      available: !!status.mock,
      selectedModel: formatProviderRef(routing.assignments['provider:mock' as const] || ''),
      selectedRef: routing.assignments['provider:mock' as const] || '',
    },
  ]
  const selectedRouteProviderId = routedDecoded?.providerId ?? 'unset'
  const selectedRouteModel = routedDecoded?.model ?? 'unset'
  const activeProviderId = String(routedProviderId || router.getActiveProviderId() || '').trim()
  let likelyCause: PreforgeForgeDiagnostics['likelyCause'] = 'unknown'

  const routerFailure = error instanceof Error && isAIError(error) ? error : null
  const attempts = routerFailure?.detail?.attempts ?? []
  const hasFailedAttempt = attempts.some((a: any) => a.status === 'failed')

  if (kind === 'mock-route' || kind === 'provider-missing' || kind === 'provider-unavailable' || kind === 'provider-disallowed' || kind === 'no-provider') {
    const hasAnyRealProvider = providers.some(entry => (
      entry.providerId === 'vertex-managed'
        ? entry.available
        : entry.enabled && entry.available
    ))

    if (hasFailedAttempt) {
      likelyCause = 'unknown' // Changed from provider_fail since it doesn't match the type
    } else if (kind === 'mock-route' || kind === 'no-provider') {
      likelyCause = hasAnyRealProvider ? 'bug' : 'settings'
    } else if (kind === 'provider-unavailable' || kind === 'provider-disallowed' || kind === 'provider-missing') {
      likelyCause = 'settings'
    }
  } else if (kind === 'sparse-response') {
    likelyCause = 'settings'
  }

  return {
    jobSlot,
    requestedProviderId,
    requestedModel,
    routedProviderId: selectedRouteProviderId,
    routedModel: selectedRouteModel,
    activeProviderId,
    providers,
    likelyCause,
  }
}
async function buildPreforgeForgeError(
  request: PreforgeDraftExpansionRequest,
  kind: PreforgeForgeFailureKind,
  message: string,
  routedProviderId: string,
  error?: unknown,
): Promise<PreforgeForgeError> {
  const diagnostics = await buildPreforgeDiagnostics(request, kind, routedProviderId, error)
  const err = new PreforgeForgeError(kind, message, diagnostics)
  return err
}
function toWorldNpc(nodeKey: string, worldForgeNpc: WorldForgeNode['npcs'][number], index: number): WorldNPC {
  const nameSlug = slugify(worldForgeNpc.name || `${nodeKey}-npc-${index + 1}`)
  return {
    id: `${slugify(nodeKey)}-npc-${index + 1}-${nameSlug}`,
    name: worldForgeNpc.name,
    identity: worldForgeNpc.identity,
    lore: worldForgeNpc.lore,
    sex: ['female', 'male'].includes(String(worldForgeNpc.sex ?? '').toLowerCase())
      ? String(worldForgeNpc.sex).toLowerCase()
      : 'unspecified',
    intentions: Array.isArray(worldForgeNpc.intentions) ? worldForgeNpc.intentions : [],
    feelings: {},
    location: nodeKey,
    relationship: Number.isFinite(worldForgeNpc.relationship) ? worldForgeNpc.relationship : 0,
    memory: Array.isArray(worldForgeNpc.memory) ? worldForgeNpc.memory : [],
    inventory: Array.isArray(worldForgeNpc.inventory) ? worldForgeNpc.inventory : [],
    activeEffects: [],
    dormantEffects: [],
  }
}

function mergePreforgeWorldForgeNode(
  state: GameState,
  node: WorldForgeNode,
  centerKey: string,
): void {
  const existing = state.atlas.nodes[node.key]
  if (!existing) return
  const mergedItems = Array.from(new Set([
    ...(existing.items ?? []),
    ...(node.items ?? []),
  ].map(item => String(item).trim()).filter(Boolean)))
  state.atlas.nodes[node.key] = {
    ...existing,
    name: node.name || existing.name,
    desc: node.desc || existing.desc,
    items: mergedItems,
    spawns: node.spawns?.length ? node.spawns : existing.spawns,
  }

  const roomNpcs = (node.npcs ?? []).map(toRoomNpc)
  if (node.key === centerKey && roomNpcs.length) {
    state.currentRoom.npcs = dedupeNpcs([...(state.currentRoom.npcs ?? []), ...roomNpcs])
  }

  if (node.npcs?.length) {
    state.world.npcs = dedupeWorldNpcs([
      ...(state.world.npcs ?? []),
      ...node.npcs.map((npc, index) => toWorldNpc(node.key, npc, index)),
    ])
  }
}

function buildPreforgeNodeContent(
  key: string,
  x: number,
  y: number,
  radius: number,
  draft: PreforgeDraft,
  recipe: PreforgeRecipe,
): AtlasNode {
  const distance = Math.abs(x) + Math.abs(y)
  const role = x === 0 && y === 0
    ? 'center'
    : Math.abs(x) === Math.abs(y)
      ? 'crossroads'
      : Math.abs(x) > Math.abs(y)
        ? 'wing'
        : 'ring'
  const roomTopic = buildTopicLabel(draft.textlets.roomSeeds || draft.textlets.worldLore || draft.brief, recipe.scale === 'large' ? 'Dense district' : 'Tester route')
  const roomLore = buildTopicLabel(draft.textlets.worldLore || draft.brief, 'Forged reality')
  const nodeName = role === 'center'
    ? buildTopicLabel(draft.seed.title || draft.brief || roomTopic, 'Preforge Center')
    : `${pickFrom(`${key}:adj`, ['Brass', 'Lantern', 'Hidden', 'Silver', 'Echoing', 'Ward', 'Archive', 'Glass'])} ${pickFrom(`${key}:noun`, ['Gate', 'Court', 'Hall', 'Route', 'Vault', 'Alley', 'Archive', 'Chamber'])}`
  const descLead = role === 'center'
    ? (draft.textlets.openingPitch || draft.brief || 'The opening room is ready.')
    : `${pickFrom(`${key}:lead`, ['A connected route', 'A reachable chamber', 'A watchful passage', 'A dense branch'])} within ${roomTopic.toLowerCase()}.`
  const descTail = distance <= 1
    ? `Nearby testers should feel the pull of ${roomLore.toLowerCase()}.`
    : distance <= 2
      ? `This node extends the opening reality outward without becoming a dead end.`
      : `This is a deeper expansion point for scale and replayability.`
  const items = distance === 0
    ? buildPreforgeItems(draft, 3, `${key}:center`)
    : distance <= 1
      ? buildPreforgeItems(draft, 2, `${key}:near`)
      : distance <= radius
        ? buildPreforgeItems(draft, 1, `${key}:far`)
        : []
  const spawns = distance >= 2
    ? [{
        archetype: pickFrom(`${key}:spawn`, ['archive wisp', 'wandering witness', 'clockwork sentinel', 'lantern courier']),
        level: distance <= 2 ? 0 : 1,
        chancePerHour: distance <= 2 ? 0.15 : 0.25,
        lootInfo: buildTopicLabel(draft.textlets.itemHooks || draft.brief, 'Useful clues and small tools'),
        friendliness: distance <= 2 ? 2 : 0,
        alertness: pickFrom(`${key}:alert`, ['watchful', 'patrolling', 'listening', 'drifting']),
      }]
    : undefined
  const flags: Record<string, string | number | boolean | null> = {
    presentation: role === 'center' ? 'hub' : role === 'crossroads' ? 'crossroads' : 'passage',
    density: distance,
  }
  return {
    name: nodeName,
    desc: `${descLead} ${descTail}`.trim(),
    items,
    flags,
    dormantFlags: [],
    clocks: distance === 0 ? buildPreforgeProgressClocks(draft) : [],
    spawns,
  }
}

function buildPreforgeGridScaffold(
  state: GameState,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
): { nodes: Record<string, AtlasNode>; edges: AtlasEdge[]; centerKey: string; extraRoomKeys: string[] } {
  const origin = state.player.location || { x: 0, y: 0, z: 0 }
  const centerKey = locationKey(origin)
  const desiredRadius = recipe.scale === 'large' ? 3 : recipe.scale === 'medium' ? 2 : 1
  const densityRadius = Math.max(1, Math.min(4, Math.ceil((Math.sqrt(Math.max(1, recipe.density.atlasNodes)) - 1) / 2)))
  const radius = Math.max(desiredRadius, densityRadius)
  const nodes: Record<string, AtlasNode> = {}
  const edges: AtlasEdge[] = []
  const extraRoomKeys: string[] = []
  const currentRoomName = String(state.currentRoom?.name ?? '').trim() || buildTopicLabel(draft.seed.title || draft.brief, 'Preforge Threshold')
  const currentRoomDesc = String(state.currentRoom?.desc ?? '').trim() || draft.textlets.openingPitch || draft.brief || 'The opening room is ready.'

  for (let dx = -radius; dx <= radius; dx += 1) {
    for (let dy = -radius; dy <= radius; dy += 1) {
      const key = locationKey({ x: origin.x + dx, y: origin.y + dy, z: origin.z })
      const scaffold = buildPreforgeNodeContent(key, dx, dy, radius, draft, recipe)
      const existing = state.atlas.nodes[key]
      const merged: AtlasNode = {
        name: existing?.name?.trim() ? existing.name : (dx === 0 && dy === 0 ? currentRoomName : scaffold.name),
        desc: existing?.desc?.trim() ? existing.desc : (dx === 0 && dy === 0 ? currentRoomDesc : scaffold.desc),
        items: Array.isArray(existing?.items) && existing.items.length ? existing.items : scaffold.items,
        flags: { ...(existing?.flags ?? {}), ...(scaffold.flags ?? {}) },
        dormantFlags: existing?.dormantFlags?.length ? existing.dormantFlags : scaffold.dormantFlags,
        clocks: existing?.clocks?.length ? existing.clocks : scaffold.clocks,
        ...(scaffold.spawns ? { spawns: scaffold.spawns } : {}),
      }
      nodes[key] = merged
      if (dx !== 0 || dy !== 0) extraRoomKeys.push(key)
      if (dx > -radius) edges.push({ x1: origin.x + dx, y1: origin.y + dy, x2: origin.x + dx + 1, y2: origin.y + dy, z1: origin.z, z2: origin.z })
      if (dy > -radius) edges.push({ x1: origin.x + dx, y1: origin.y + dy, x2: origin.x + dx, y2: origin.y + dy + 1, z1: origin.z, z2: origin.z })
    }
  }

  return { nodes, edges, centerKey, extraRoomKeys }
}

function isPlaceholderRoomName(value: string): boolean {
  const lowered = String(value ?? '').trim().toLowerCase()
  return !lowered || lowered === 'void' || lowered === 'unknown place'
}

function countPreforgeDensity(state: GameState): PreforgeRecipe['density'] {
  return {
    atlasNodes: Object.keys(state.atlas.nodes ?? {}).length,
    rooms: Object.keys(state.atlas.nodes ?? {}).length,
    npcs: state.world.npcs.length + state.currentRoom.npcs.length,
    companions: state.companions.length,
    items: Object.values(state.atlas.nodes ?? {}).reduce((sum, node) => sum + ((node?.items?.length ?? 0)), 0) + (state.currentRoom.items?.length ?? 0),
    quests: state.journal.length,
    clocks: (state.world.clocks?.length ?? 0) + (state.currentRoom.clocks?.length ?? 0),
    events: state.worldLog.length,
  }
}

function reportStatusFor(actual: number, requested: number): 'below' | 'met' | 'exceeded' {
  if (actual < requested) return 'below'
  if (actual > requested) return 'exceeded'
  return 'met'
}

function buildPreforgeReportMeasures(
  requested: PreforgeRecipe['density'],
  actual: PreforgeRecipe['density'],
): PreforgeForgeReport['measures'] {
  const labels: Array<[keyof PreforgeRecipe['density'], string]> = [
    ['atlasNodes', 'Atlas Nodes'],
    ['rooms', 'Rooms'],
    ['npcs', 'NPCs'],
    ['companions', 'Companions'],
    ['items', 'Items'],
    ['quests', 'Quests'],
    ['clocks', 'Clocks'],
    ['events', 'Events'],
  ]
  return labels.map(([key, label]) => {
    const requestedValue = Number(requested[key] ?? 0)
    const actualValue = Number(actual[key] ?? 0)
    return {
      label,
      requested: requestedValue,
      actual: actualValue,
      delta: actualValue - requestedValue,
      status: reportStatusFor(actualValue, requestedValue),
    }
  })
}

function buildPreforgeReportDocument(
  title: string,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
  actual: PreforgeRecipe['density'],
  measures: PreforgeForgeReport['measures'],
): string {
  const lines = [
    `# ${title}`,
    '',
    '## Overview',
    draft.intentSummary || draft.textlets.openingPitch || draft.brief || 'A tester-ready reality has been forged.',
    '',
    '## Extracted Intent',
    draft.analysis.scopeSummary || 'No scope summary captured.',
    '',
    '## Requested Shape',
    `Source world: ${recipe.sourceWorld.kind}${recipe.sourceWorld.kind === 'liveRun' || recipe.sourceWorld.kind === 'savedWorld' ? `:${recipe.sourceWorld.saveId}` : recipe.sourceWorld.kind === 'templateWorld' ? `:${recipe.sourceWorld.templateId}` : `:${recipe.sourceWorld.presetId}`}`,
    `Scale: ${recipe.scale}`,
    `Tone: ${recipe.tone.mood} / danger ${recipe.tone.dangerLevel} / social ${recipe.tone.socialFocus} / exploration ${recipe.tone.explorationFocus} / combat ${recipe.tone.combatFocus}`,
    `Output mode: ${recipe.outputMode}`,
    '',
    '## Requested vs Actual',
    ...measures.map(measure => `- ${measure.label}: requested ${measure.requested}, actual ${measure.actual}, delta ${measure.delta >= 0 ? '+' : ''}${measure.delta} (${measure.status})`),
    '',
    '## Measured Content',
    `World seed: ${draft.seed.summary || draft.brief || 'n/a'}`,
    `Seed artifact: ${draft.seed.title || draft.seed.code || 'n/a'}`,
    `Vessel artifact: ${draft.vessel.name || draft.vessel.code || 'n/a'} · ${draft.vessel.identity || 'n/a'}`,
    `Key entities: ${draft.analysis.keyEntities.join(', ') || 'n/a'}`,
    `Room threads: ${draft.textlets.roomSeeds || 'n/a'}`,
    `NPC threads: ${draft.textlets.npcThreads || 'n/a'}`,
    `Quest threads: ${draft.textlets.questThreads || 'n/a'}`,
    `Item hooks: ${draft.textlets.itemHooks || 'n/a'}`,
    `Atlas nodes: ${actual.atlasNodes}`,
    `Rooms: ${actual.rooms}`,
    `NPCs: ${actual.npcs}`,
    `Companions: ${actual.companions}`,
    `Items: ${actual.items}`,
    `Quests: ${actual.quests}`,
    `Clocks: ${actual.clocks}`,
    `Events: ${actual.events}`,
    '',
    '## Compliance Notes',
    ...measures
      .filter(measure => measure.status !== 'below')
      .map(measure => `${measure.label} reached ${measure.actual} against a request of ${measure.requested}.`),
    ...measures
      .filter(measure => measure.status === 'below')
      .map(measure => `${measure.label} fell short of the request at ${measure.actual}/${measure.requested}.`),
  ]
  return lines.join('\n').trim()
}

function buildPreforgeCommercialCopy(
  title: string,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
  actual: PreforgeRecipe['density'],
): string {
  const opening = draft.textlets.openingPitch || draft.brief || 'A dense new reality is ready.'
  return [
    `${title}: ${draft.intentSummary || opening}`,
    `Forged from ${recipe.sourceWorld.kind}${recipe.sourceWorld.kind === 'liveRun' || recipe.sourceWorld.kind === 'savedWorld' ? `:${recipe.sourceWorld.saveId}` : recipe.sourceWorld.kind === 'templateWorld' ? `:${recipe.sourceWorld.templateId}` : `:${recipe.sourceWorld.presetId}`}.`,
    `Built to ${actual.atlasNodes} atlas nodes, ${actual.npcs} NPCs, ${actual.items} items, and ${actual.quests} quests.`,
    `Seed: ${draft.seed.title || draft.seed.summary || draft.seed.code || 'n/a'} · Vessel: ${draft.vessel.name || draft.vessel.code || 'n/a'}.`,
  ].join(' ')
}

export function buildPreforgeForgeReport(
  title: string,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
  actual: PreforgeRecipe['density'],
): PreforgeForgeReport {
  const measures = buildPreforgeReportMeasures(recipe.density, actual)
  const reportTitle = `${title} Forge Report`
  return {
    title: reportTitle,
    document: buildPreforgeReportDocument(reportTitle, recipe, draft, actual, measures),
    commercial: buildPreforgeCommercialCopy(title, recipe, draft, actual),
    requested: recipe.density,
    actual,
    measures,
    compliance: measures.map(measure => `${measure.label}: ${measure.status}`),
    notes: [
      `Generated from ${recipe.outputMode} on ${new Date().toISOString()}.`,
      'Editable by an admin before publish or reuse as world description copy.',
    ],
    generatedAtMs: Date.now(),
  }
}

export function materializePreforgeRealityState(
  sourceState: GameState,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
): GameState {
  const state = structuredClone(sourceState)
  const scaffold = buildPreforgeGridScaffold(state, recipe, draft)
  state.world.seed = draft.seed.summary || draft.brief || state.world.seed || draft.seed.code || 'preforge'
  state.world.narrative = draft.textlets.worldLore || draft.brief || state.world.narrative || ''

  state.atlas.nodes = {
    ...state.atlas.nodes,
    ...scaffold.nodes,
  }
  state.atlas.edges = dedupeEdges([...state.atlas.edges, ...scaffold.edges])

  const roomItems = compactPhrases(draft.textlets.itemHooks, draft.textlets.roomSeeds, draft.textlets.worldLore)
  const generatedRoomNpcs = buildPreforgeRoomNpcs(draft)
  const currentRoom = {
    ...state.currentRoom,
    name: isPlaceholderRoomName(state.currentRoom.name) ? buildTopicLabel(draft.seed.title || draft.brief, 'Preforge Threshold') : state.currentRoom.name,
    desc: draft.textlets.openingPitch || draft.brief || state.currentRoom.desc || 'The opening room is ready.',
    items: Array.from(new Set([
      ...(state.currentRoom.items ?? []),
      ...buildPreforgeItems(draft, Math.max(2, Math.min(4, roomItems.length || 2)), 'room'),
    ])),
    npcs: dedupeNpcs([...(state.currentRoom.npcs ?? []), ...generatedRoomNpcs]),
    dormantFlags: state.currentRoom.dormantFlags ?? [],
    clocks: Array.from(new Map([...(state.currentRoom.clocks ?? []), ...buildPreforgeProgressClocks(draft)].map(clock => [clock.id, clock])).values()),
  }
  state.currentRoom = currentRoom
  state.world.npcs = dedupeWorldNpcs([...(state.world.npcs ?? []), ...buildPreforgeWorldNpcs(draft, scaffold.centerKey, scaffold.extraRoomKeys)])
  state.journal = [...state.journal, ...buildPreforgeQuestThreads(draft)]
    .filter((quest, index, arr) => arr.findIndex(item => item.id === quest.id) === index)
  state.worldLog = [
    ...(state.worldLog ?? []),
    {
      time: new Date().toISOString(),
      text: draft.textlets.openingPitch || draft.brief || 'A tester-ready reality is ready.',
      type: 'world' as const,
    },
  ].slice(-30)
  state.world.clocks = Array.from(new Map([...(state.world.clocks ?? []), ...buildPreforgeProgressClocks(draft)].map(clock => [clock.id, clock])).values())
  state.history = state.history.length ? state.history : [{
    location: currentRoom.name,
    time: new Date().toISOString(),
    text: draft.textlets.openingPitch || draft.brief || 'The reality opens.',
    suggestions: [],
    context: { items: [...currentRoom.items], npcs: [...currentRoom.npcs] },
  }]
  state.meta.timestamp = Date.now()
  return state
}

function collectPreforgeScaffoldKeys(state: GameState): string[] {
  return Object.entries(state.atlas.nodes ?? {})
    .filter(([, node]) => Boolean(node?.flags?.presentation) && typeof node?.flags?.density === 'number')
    .map(([key]) => key)
}

export async function enrichPreforgeRealityState(
  state: GameState,
  recipe: PreforgeRecipe,
  draft: PreforgeDraft,
): Promise<GameState> {
  const scaffoldKeys = collectPreforgeScaffoldKeys(state)
  if (!scaffoldKeys.length) return state

  const worldSeed = draft.seed.summary || draft.brief || state.world.seed || draft.seed.code || 'preforge'
  const startingRoom = draft.seed.title || state.currentRoom.name || 'Preforge Threshold'
  const centerKey = locationKey(state.player.location)

  try {
    const forgedNodes = await runWorldForge(worldSeed, startingRoom, scaffoldKeys, {
      brief: draft.brief,
      recipe,
      draft,
    })

    if (!Array.isArray(forgedNodes) || !forgedNodes.length) return state

    for (const node of forgedNodes) {
      mergePreforgeWorldForgeNode(state, node, centerKey)
    }

    const centerNode = forgedNodes.find(node => node.key === centerKey)
    if (centerNode) {
      state.currentRoom.name = centerNode.name || state.currentRoom.name
      state.currentRoom.desc = centerNode.desc || state.currentRoom.desc
      state.currentRoom.items = Array.from(new Set([
        ...(state.currentRoom.items ?? []),
        ...(centerNode.items ?? []),
      ].map(item => String(item).trim()).filter(Boolean)))
    }

    state.worldLog = [
      ...(state.worldLog ?? []),
      {
        time: new Date().toISOString(),
        text: `World Forge enriched ${forgedNodes.length} scaffold nodes from ${startingRoom}.`,
        type: 'world' as const,
      },
    ].slice(-30)
  } catch (error) {
    console.warn('[Preforge] World Forge enrichment fell back to the scaffold:', error)
  }

  return state
}

function dedupeEdges(edges: AtlasEdge[]): AtlasEdge[] {
  const seen = new Set<string>()
  const deduped: AtlasEdge[] = []
  for (const edge of edges) {
    const key = `${edge.x1},${edge.y1},${edge.z1 ?? 0}-${edge.x2},${edge.y2},${edge.z2 ?? 0}`
    const reverse = `${edge.x2},${edge.y2},${edge.z2 ?? 0}-${edge.x1},${edge.y1},${edge.z1 ?? 0}`
    if (seen.has(key) || seen.has(reverse)) continue
    seen.add(key)
    deduped.push(edge)
  }
  return deduped
}

function buildFallbackTextlets(brief: string, recipe: PreforgeRecipe): PreforgeDraftTextlets {
  const theme = `${recipe.tone.mood} / ${recipe.tone.dangerLevel} / ${recipe.scale}`
  const density = `${recipe.density.rooms} rooms, ${recipe.density.npcs} NPCs, ${recipe.density.items} items`
  return {
    worldLore: `${brief || 'A tester-ready reality'} — ${theme}.`,
    openingPitch: `A dense ${recipe.outputMode.replace('-', ' ')} with ${density}.`,
    npcThreads: `Generate varied NPC roles that echo the brief: ${brief || 'keep it specific, social, and reusable'}.`,
    questThreads: `Create 2-4 quest threads with clear goals, friction, and short-term payoff.`,
    itemHooks: `Seed useful items, proofs, keys, tools, and artifacts that can be reused across scenes.`,
    roomSeeds: `Make the opening spaces readable, connected, and easy to expand later.`,
  }
}

function splitAnalysisLines(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map(entry => String(entry ?? '').trim()).filter(Boolean)
    : String(value ?? '')
        .split(/[\n,;|]+/)
        .map(entry => entry.trim())
        .filter(Boolean)
}

function buildFallbackAnalysis(brief: string, recipe: PreforgeRecipe, draft?: Partial<PreforgeDraft>): PreforgeDraftAnalysis {
  const keyEntities = compactPhrases(
    brief,
    String(draft?.textlets?.worldLore ?? ''),
    String(draft?.textlets?.openingPitch ?? ''),
    String(draft?.textlets?.npcThreads ?? ''),
    String(draft?.textlets?.questThreads ?? ''),
  )
  const analysisBrief = brief || draft?.textlets?.openingPitch || 'Dense tester-ready reality'
  return {
    intentSummary: analysisBrief,
    scopeSummary: `${recipe.scale} ${recipe.outputMode.replace('-', ' ')} reality with ${recipe.density.rooms} rooms, ${recipe.density.npcs} NPCs, and reusable seed/vessel artifacts.`,
    keyEntities: keyEntities.length ? keyEntities.slice(0, 8) : compactPhrases('world', 'seed', 'vessel', 'opening route'),
    assumptions: compactPhrases(
      draft?.textlets?.openingPitch ? `Opening pitch preserved from brief: ${draft.textlets.openingPitch}` : '',
      `Use ${recipe.scale} scale with ${recipe.density.rooms} rooms and ${recipe.density.npcs} NPCs.`,
    ),
    ambiguities: compactPhrases(
      draft?.textlets?.npcThreads ? '' : 'NPC identities remain open and should be clarified during review.',
      draft?.textlets?.questThreads ? '' : 'Quest structure is broad and should be clarified.',
    ).slice(0, 8),
    generationNotes: compactPhrases(
      `Preserve the source brief: ${analysisBrief}.`,
      `Prefer dense tester-ready output with ${recipe.outputMode.replace('-', ' ')} delivery.`,
      `Respect tone ${recipe.tone.mood} and danger ${recipe.tone.dangerLevel}.`,
    ),
  }
}

function buildFallbackPreforgeDraft(request: PreforgeDraftExpansionRequest): PreforgeDraftExpansionResult {
  const brief = String(request.brief ?? '').trim()
  const sourceSlug = request.sourceWorld.kind === 'liveRun' || request.sourceWorld.kind === 'savedWorld'
    ? request.sourceWorld.saveId
    : request.sourceWorld.kind === 'templateWorld'
      ? request.sourceWorld.templateId
      : request.sourceWorld.presetId
  const baseSlug = slugify(brief || sourceSlug || 'preforge')
  const draft: PreforgeDraft = {
    brief,
    intentSummary: brief || request.draft?.intentSummary || 'Dense tester-ready reality',
    analysis: buildFallbackAnalysis(brief, request.recipe, request.draft),
    seed: {
      mode: 'create',
      code: request.draft?.seed?.code || `${baseSlug}-seed`,
      title: request.draft?.seed?.title || `${baseSlug.replace(/-/g, ' ')} seed`,
      summary: request.draft?.seed?.summary || brief || 'Generated seed for a tester-ready reality.',
    },
    vessel: {
      mode: 'create',
      code: request.draft?.vessel?.code || `${baseSlug}-vessel`,
      name: request.draft?.vessel?.name || 'Traveler',
      identity: request.draft?.vessel?.identity || 'A forged protagonist shaped by the brief.',
      lineage: request.draft?.vessel?.lineage || '',
      sex: request.draft?.vessel?.sex || 'unspecified',
      bio: request.draft?.vessel?.bio || brief || 'A vessel profile ready for review.',
    },
    textlets: request.draft?.textlets ?? buildFallbackTextlets(brief, request.recipe),
    ...(request.draft?.generatedAtMs ? { generatedAtMs: request.draft.generatedAtMs } : {}),
  }
  return {
    draft,
    recipe: request.recipe,
    sourceWorld: request.sourceWorld,
    notes: [
      'Fallback draft used because no AI provider returned structured output.',
      'Review the generated seed and vessel fields before publishing.',
    ],
  }
}

export async function expandPreforgeDraft(request: PreforgeDraftExpansionRequest): Promise<PreforgeDraftExpansionResult> {
  const brief = String(request.brief ?? '').trim()
  const progress = (stage: PreforgeDraftProgressStage, message: string): void => {
    try {
      request.onProgress?.({ stage, message })
    } catch (error) {
      console.warn('[Preforge] Progress callback failed:', error)
    }
  }
  if (!brief) {
    throw new Error('A brief is required before forging.')
  }
  try {
    progress('reading-brief', 'Reading your brief and isolating the core intent.')
    progress('building-prompt', 'Building the structured extraction prompt.')
    const aiSelection = request.recipe.aiSelection
    const aiLabel = aiSelection.providerId === 'auto'
      ? 'the extractor'
      : aiSelection.model
        ? `${aiSelection.providerId}:${aiSelection.model}`
        : aiSelection.providerId
    progress('requesting-draft', `Sending the brief to ${aiLabel}.`)
    await new Promise<void>((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          setTimeout(() => resolve(), 120)
        })
        return
      }
      setTimeout(resolve, 120)
    })

    let accumulated = ''
    let lastProgressMs = Date.now()
    let chunkCount = 0
    for await (const chunk of router.stream({
      jobType: 'preforge_draft',
      systemPrompt: PREFORGE_DRAFT_SYSTEM,
      userPrompt: buildPreforgeDraftPrompt(brief, request.recipe, request.draft, request.sourceWorld),
      providerId: aiSelection.providerId,
      model: aiSelection.model || undefined,
    })) {
      accumulated += chunk
      chunkCount++
      const now = Date.now()
      if (now - lastProgressMs > 800) {
        progress('receiving-draft', `Receiving structured data... (${accumulated.length} bytes)`)
        lastProgressMs = now
      }
    }

    if (!accumulated.trim()) {
      throw new Error('The AI provider returned an empty draft.')
    }

    const result = extractJson(accumulated) as unknown
    if (!isMockProviderAllowed(request) && isProviderMock()) {
      progress('blocked', 'AI routing fell back to the mock provider, which is not allowed for forging.')
      throw await buildPreforgeForgeError(request, 'mock-route', buildPreforgeStrictFailureMessage('route'), router.getActiveProviderId() ?? 'mock')
    }
    if (!isPreforgeDraftShapeAdequate(result)) {
      progress('blocked', 'The AI response was too sparse to trust for forging.')
      throw await buildPreforgeForgeError(request, 'sparse-response', buildPreforgeStrictFailureMessage('shape'), router.getActiveProviderId() ?? 'unknown')
    }
    progress('normalizing-draft', 'Normalizing seed, vessel, and textlets.')
    const normalized = normalizePreforgeDraftResult(result, request)
    progress('ready', 'Structured draft ready for review.')
    return normalized
  } catch (error) {
    console.warn('[Preforge] Draft expansion failed:', error)
    if (error instanceof PreforgeForgeError) {
      throw error
    }
    const message = String(error instanceof Error ? error.message : error ?? '').trim()
    const cleanProviderMessage = buildPreforgeStrictFailureMessage('provider')
    const cleanRouteMessage = buildPreforgeStrictFailureMessage('route')
    const cleanShapeMessage = buildPreforgeStrictFailureMessage('shape')
    const activeId = router.getActiveProviderId() ?? 'unknown'

    if (isAIError(error)) {
      if (error.code === 'provider-not-registered' || error.code === 'config-missing') {
        throw await buildPreforgeForgeError(request, 'provider-missing', error.message, activeId, error)
      }
      if (error.code === 'provider-unavailable') {
        throw await buildPreforgeForgeError(request, 'provider-unavailable', error.message, activeId, error)
      }
      if (error.code === 'provider-disallowed') {
        throw await buildPreforgeForgeError(request, 'provider-disallowed', error.message, activeId, error)
      }
      if (error.code === 'no-provider') {
        throw await buildPreforgeForgeError(request, 'no-provider', cleanProviderMessage, activeId, error)
      }
      if (error.code === 'mock-route') {
        throw await buildPreforgeForgeError(request, 'mock-route', cleanRouteMessage, activeId, error)
      }
      if (error.code === 'response-invalid-shape') {
        throw await buildPreforgeForgeError(request, 'sparse-response', cleanShapeMessage, activeId, error)
      }
    }

    const requestedProviderMessage = /\[AIRouter\]\s*Requested provider/i.test(message)
    if (message.includes(cleanProviderMessage) || message.includes(cleanRouteMessage) || message.includes(cleanShapeMessage)) {
      throw await buildPreforgeForgeError(
        request,
        requestedProviderMessage ? 'provider-missing' : 'unknown',
        message,
        activeId,
        error
      )
    }
    if (/not available/i.test(message)) {
      throw await buildPreforgeForgeError(request, 'provider-unavailable', message, activeId, error)
    }
    if (/not allowed/i.test(message)) {
      throw await buildPreforgeForgeError(request, 'provider-disallowed', message, activeId, error)
    }
    if (/not registered/i.test(message)) {
      throw await buildPreforgeForgeError(request, 'provider-missing', message, activeId, error)
    }
    if (requestedProviderMessage) {
      throw await buildPreforgeForgeError(
        request,
        'provider-missing',
        `${message.replace(/^\[AIRouter\]\s*/, '')} Open AI settings and try again.`,
        activeId,
        error
      )
    }
    if (/No provider available|failed for preforge_draft|no real provider/i.test(message)) {
      throw await buildPreforgeForgeError(request, 'no-provider', cleanProviderMessage, activeId, error)
    }
    throw await buildPreforgeForgeError(request, 'unknown', 'The AI forge could not complete. Open AI settings and try again.', activeId, error)
  }
}

function normalizePreforgeDraftTextlets(raw: unknown): PreforgeDraftTextlets {
  const entry = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  return {
    worldLore: String(entry.worldLore ?? '').trim(),
    openingPitch: String(entry.openingPitch ?? '').trim(),
    npcThreads: String(entry.npcThreads ?? '').trim(),
    questThreads: String(entry.questThreads ?? '').trim(),
    itemHooks: String(entry.itemHooks ?? '').trim(),
    roomSeeds: String(entry.roomSeeds ?? '').trim(),
  }
}

function normalizePreforgeDraftResult(raw: unknown, request: PreforgeDraftExpansionRequest): PreforgeDraftExpansionResult {
  const entry = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  const sourceWorld = request.sourceWorld
  const recipe = request.recipe
  const brief = String(entry.brief ?? request.brief ?? '').trim()
  const sourceRecipe = entry.recipe && typeof entry.recipe === 'object' ? entry.recipe as Record<string, unknown> : {}
  const sourceSeed = entry.seed && typeof entry.seed === 'object' ? entry.seed as Record<string, unknown> : {}
  const sourceVessel = entry.vessel && typeof entry.vessel === 'object' ? entry.vessel as Record<string, unknown> : {}
  const sourceAnalysis = entry.analysis && typeof entry.analysis === 'object' ? entry.analysis as Record<string, unknown> : {}
  const fallbackAnalysis = buildFallbackAnalysis(brief, recipe, request.draft)
  const keyEntities = splitAnalysisLines(sourceAnalysis.keyEntities ?? request.draft?.analysis?.keyEntities)
  const assumptions = splitAnalysisLines(sourceAnalysis.assumptions ?? request.draft?.analysis?.assumptions)
  const ambiguities = splitAnalysisLines(sourceAnalysis.ambiguities ?? request.draft?.analysis?.ambiguities)
  const generationNotes = splitAnalysisLines(sourceAnalysis.generationNotes ?? request.draft?.analysis?.generationNotes)
  const draft: PreforgeDraft = {
    brief,
    intentSummary: String(entry.intentSummary ?? sourceAnalysis.intentSummary ?? request.draft?.intentSummary ?? brief).trim() || brief,
    analysis: {
      intentSummary: String(entry.intentSummary ?? sourceAnalysis.intentSummary ?? request.draft?.intentSummary ?? brief).trim() || brief,
      scopeSummary: String(sourceAnalysis.scopeSummary ?? request.draft?.analysis?.scopeSummary ?? '').trim() || fallbackAnalysis.scopeSummary,
      keyEntities: keyEntities.length ? keyEntities : fallbackAnalysis.keyEntities,
      assumptions: assumptions.length ? assumptions : fallbackAnalysis.assumptions,
      ambiguities: ambiguities.length ? ambiguities : fallbackAnalysis.ambiguities,
      generationNotes: generationNotes.length ? generationNotes : fallbackAnalysis.generationNotes,
    },
    seed: {
      mode: String(sourceSeed.mode ?? request.draft?.seed?.mode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create',
      code: String(sourceSeed.code ?? request.draft?.seed?.code ?? '').trim(),
      title: String(sourceSeed.title ?? request.draft?.seed?.title ?? '').trim(),
      summary: String(sourceSeed.summary ?? request.draft?.seed?.summary ?? '').trim(),
    },
    vessel: {
      mode: String(sourceVessel.mode ?? request.draft?.vessel?.mode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create',
      code: String(sourceVessel.code ?? request.draft?.vessel?.code ?? '').trim(),
      name: String(sourceVessel.name ?? request.draft?.vessel?.name ?? '').trim(),
      identity: String(sourceVessel.identity ?? request.draft?.vessel?.identity ?? '').trim(),
      lineage: String(sourceVessel.lineage ?? request.draft?.vessel?.lineage ?? '').trim(),
      sex: ['female', 'male', 'unspecified'].includes(String(sourceVessel.sex ?? request.draft?.vessel?.sex ?? 'unspecified').toLowerCase())
        ? String(sourceVessel.sex ?? request.draft?.vessel?.sex ?? 'unspecified').toLowerCase() as PreforgeDraft['vessel']['sex']
        : 'unspecified',
      bio: String(sourceVessel.bio ?? request.draft?.vessel?.bio ?? '').trim(),
    },
    textlets: normalizePreforgeDraftTextlets(entry.textlets ?? request.draft?.textlets),
    ...(Number.isFinite(entry.generatedAtMs) ? { generatedAtMs: Number(entry.generatedAtMs) } : {}),
  }
  return {
    draft,
    recipe: sourceRecipe ? { ...recipe, ...(sourceRecipe as object) } as PreforgeRecipe : recipe,
    sourceWorld,
    notes: Array.isArray(entry.notes) ? entry.notes.map(note => String(note ?? '').trim()).filter(Boolean).slice(0, 8) : [],
  }
}

function buildLivePreforgeRecipe(sourceState: typeof gameStore.state, saveId: string): PreforgeRecipe {
  const atlasNodes = countAtlasNodes(sourceState)
  const rooms = atlasNodes
  const worldNpcs = sourceState?.world?.npcs
  const companionsList = sourceState?.companions
  const journal = sourceState?.journal
  const clocksList = sourceState?.world?.clocks
  const worldLog = sourceState?.worldLog
  const npcs = Array.isArray(worldNpcs) ? worldNpcs.length : 0
  const companions = Array.isArray(companionsList) ? companionsList.length : 0
  const items = countAtlasItems(sourceState)
  const quests = Array.isArray(journal) ? journal.length : 0
  const clocks = Array.isArray(clocksList) ? clocksList.length : 0
  const events = Array.isArray(worldLog) ? worldLog.length : 0
  const scale = atlasNodes >= 30 || npcs >= 12 ? 'large' : atlasNodes >= 12 || npcs >= 6 ? 'medium' : 'small'
  const sourceWorld = { kind: 'liveRun' as const, saveId: saveId || String(Date.now()) }
  const recipe = buildPreforgeRecipeDefaults(sourceWorld, scale, 'full-reality')
  return {
    ...recipe,
    density: {
      ...recipe.density,
      atlasNodes,
      rooms,
      npcs,
      companions,
      items,
      quests,
      clocks,
      events,
    },
    constraints: {
      ...recipe.constraints,
      minimumContentFloors: {
        atlasNodes: Math.max(1, Math.floor(atlasNodes * 0.75)),
        rooms: Math.max(1, Math.floor(rooms * 0.75)),
        npcs: Math.max(1, Math.floor(npcs * 0.75)),
        companions: Math.max(0, Math.floor(companions * 0.5)),
        items: Math.max(1, Math.floor(items * 0.75)),
        quests: Math.max(1, Math.floor(quests * 0.75)),
        clocks: Math.max(0, Math.floor(clocks * 0.5)),
        events: Math.max(0, Math.floor(events * 0.5)),
      },
    },
  }
}

function buildLivePreforgeDraft(sourceState: typeof gameStore.state, recipe: PreforgeRecipe, codes: PreforgeCodes): PreforgeDraft {
  const roomName = String(sourceState?.currentRoom?.name ?? '').trim()
  const worldSeed = String(sourceState?.world?.seed ?? '').trim()
  const npcNames = (sourceState?.currentRoom?.npcs ?? []).slice(0, 4).map(n => String(n?.name ?? '').trim()).filter(Boolean)
  const questNames = (sourceState?.journal ?? []).slice(0, 4).map(q => String(q?.title ?? '').trim()).filter(Boolean)
  const itemNames = Object.values(sourceState?.atlas?.nodes ?? {})
    .flatMap(node => Array.isArray(node?.items) ? node.items : [])
    .map(item => typeof item === 'string' ? item.trim() : String((item as { name?: string } | null)?.name ?? '').trim())
    .filter(Boolean)
    .slice(0, 6)
  const intentSummary = worldSeed || roomName || 'Live capture from current run.'
  const analysis = {
    intentSummary,
    scopeSummary: `Live capture from save ${codes.starterCode} with ${recipe.scale} scale and ${recipe.outputMode.replace('-', ' ')} output.`,
    keyEntities: compactPhrases(worldSeed, roomName, ...npcNames, ...questNames).slice(0, 10),
    assumptions: [
      `The current run already contains ${recipe.density.atlasNodes} atlas nodes.`,
      'Use the live state as the source world.',
    ],
    ambiguities: itemNames.length ? [] : ['No explicit item anchors found in the live run.'],
    generationNotes: [
      'Prefer concrete reuse of current nodes, rooms, and characters.',
      'Preserve the live run identity so testers can launch back into a recognizable reality.',
    ],
  }
  return {
    brief: intentSummary,
    intentSummary,
    analysis,
    seed: {
      mode: 'create',
      code: codes.seedCode,
      title: worldSeed ? worldSeed.split('\n')[0].trim().slice(0, 80) : `${codes.worldCode} seed`,
      summary: worldSeed || roomName || 'Seed captured from the live run.',
    },
    vessel: {
      mode: 'create',
      code: codes.vesselCode,
      name: String(sourceState?.player?.name ?? 'Traveler').trim() || 'Traveler',
      identity: String(sourceState?.player?.identity ?? 'Live vessel').trim() || 'Live vessel',
      lineage: '',
      sex: ['female', 'male'].includes(String(sourceState?.player?.sex ?? '').toLowerCase())
        ? String(sourceState?.player?.sex).toLowerCase() as PreforgeDraft['vessel']['sex']
        : 'unspecified',
      bio: String(sourceState?.player?.bio ?? '').trim() || 'Captured from the live run.',
    },
    textlets: {
      worldLore: String(sourceState?.world?.narrative ?? '').trim() || `Dense live reality derived from ${worldSeed || roomName || 'the active run'}.`,
      openingPitch: `Launch into ${roomName || 'the current room'} with an already seeded world and vessel.`,
      npcThreads: npcNames.length ? `Key NPCs: ${npcNames.join(', ')}` : 'Generate or preserve meaningful NPC threads.',
      questThreads: questNames.length ? `Quest hooks: ${questNames.join(', ')}` : 'Add at least one quest thread tied to the source world.',
      itemHooks: itemNames.length ? `Items already present: ${itemNames.join(', ')}` : 'Seed reusable items, proofs, and tools.',
      roomSeeds: roomName ? `Opening room: ${roomName}` : 'Describe the opening room and its outward connections.',
    },
  }
}

export function derivePreforgeCodes(baseId: string): PreforgeCodes {
  const id = normalizeBundleId(baseId)
  if (!id) throw new Error('Preforge bundle id is required.')
  return {
    worldCode: `${id}-world`,
    seedCode: `${id}-seed`,
    vesselCode: `${id}-vessel`,
    starterCode: `${id}-starter`,
  }
}

export async function capturePreforgedRealityBundle(request: PreforgeCaptureRequest): Promise<PreforgeCaptureResult> {
  const sourceState = gameStore.state
  if (!sourceState) {
    throw new Error('Open a live run before capturing a preforged bundle.')
  }

  const id = normalizeBundleId(request.id)
  if (!id) throw new Error('Preforge bundle id is required.')
  const title = String(request.title ?? '').trim()
  if (!title) throw new Error('Preforge title is required.')

  const codes = derivePreforgeCodes(id)
  const draftSeedRecipe = request.forgeRecipe ?? buildLivePreforgeRecipe(sourceState, gameStore.activeSaveId || id)
  const preforgeDraft = buildLivePreforgeDraft(sourceState, draftSeedRecipe, codes)
  const materializedState = materializePreforgeRealityState(sourceState, draftSeedRecipe, preforgeDraft)
  const denseState = await enrichPreforgeRealityState(materializedState, draftSeedRecipe, preforgeDraft)
  const denseDensity = countPreforgeDensity(denseState)
  const forgeReport = buildPreforgeForgeReport(title, draftSeedRecipe, preforgeDraft, denseDensity)
  const forgeRecipe = {
    ...(request.forgeRecipe ?? draftSeedRecipe),
    density: denseDensity,
    constraints: {
      ...(request.forgeRecipe ?? draftSeedRecipe).constraints,
      minimumContentFloors: {
        atlasNodes: Math.max(1, Math.floor(denseDensity.atlasNodes * 0.75)),
        rooms: Math.max(1, Math.floor(denseDensity.rooms * 0.75)),
        npcs: Math.max(1, Math.floor(denseDensity.npcs * 0.75)),
        companions: Math.max(0, Math.floor(denseDensity.companions * 0.5)),
        items: Math.max(1, Math.floor(denseDensity.items * 0.75)),
        quests: Math.max(1, Math.floor(denseDensity.quests * 0.75)),
        clocks: Math.max(0, Math.floor(denseDensity.clocks * 0.5)),
        events: Math.max(0, Math.floor(denseDensity.events * 0.5)),
      },
    },
  } satisfies PreforgeRecipe
  await Promise.all([
    exportBlueprint(codes.worldCode, denseState),
    exportBlueprint(codes.seedCode, denseState),
    exportVesselProfile(codes.vesselCode, denseState),
    exportStarterKit(codes.starterCode, denseState),
  ])

  const offer = normalizePreforgedOffer({
    id,
    kind: 'reality',
    title,
    summary: String(request.summary ?? '').trim() || forgeReport.commercial,
    worldManifestId: codes.worldCode,
    seedCode: codes.seedCode,
    vesselCode: codes.vesselCode,
    starterCode: codes.starterCode,
    includedPlanId: request.includedPlanId ?? 'pro',
    priceModel: request.priceModel ?? 'included',
    priceCents: request.priceModel === 'paid' ? Number(request.priceCents || 0) : 0,
    currency: String(request.currency ?? 'USD').trim().toUpperCase() || 'USD',
    publishState: request.publishState ?? 'draft',
    forgeRecipe,
    preforgeDraft,
    forgeReport,
  })!

  return { ...codes, offer }
}

export async function launchPreforgedReality(offer: PreforgedOffer): Promise<PreforgeLaunchResult> {
  const starterCode = String(offer.starterCode ?? '').trim()
  if (!starterCode) {
    throw new Error('Preforged reality is missing a starter code.')
  }

  const result = await gameStore.execImport(starterCode)
  if (result.type !== 'starter' || !result.worldData || !result.playerData) {
    throw new Error('Preforged starter bundle could not be loaded.')
  }

  const started = await gameStore.startNewGame({
    name: result.playerData.name,
    identity: result.playerData.identity,
    sex: result.playerData.sex,
    bio: result.playerData.bio,
    seed: result.worldData.seed,
    worldData: result.worldData,
    playerData: result.playerData,
  })

  return { saveId: started.saveId }
}
